import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { useConfigurations } from 'query/useConfigurations'
import { useCreateUser, useUpdateUser, useUser } from 'query/useUsers'
import {
  Alert,
  Button,
  Field,
  Form,
  FormActions,
  FormCard,
  Input,
  LoadingIndicator,
  PageContent,
  PageHeader,
  Select,
} from 'ui'

import DeleteUserConfirmModal from './DeleteUserConfirmModal'
import { buildEditableUserPayload } from './userPayload'

const initialUserState = {
  configurationId: 0,
  email: '',
  errors: [],
  password: '',
  provider: 'oauth',
  type: 'regular',
}

export const UsersEdit = ({ isNew: isNewUser = false }) => {
  const navigate = useNavigate()
  const { id: userId } = useParams()
  const [userState, setUserState] = useState(initialUserState)
  const [loadedUserId, setLoadedUserId] = useState(isNewUser ? userId : null)
  const { data: configurations, isError: isConfigurationsError, isLoading: isConfigurationsLoading } = useConfigurations()
  const { data: user, isError: isUserError, isLoading: isUserLoading } = useUser(userId)

  const handleSaveSuccess = response => {
    if (response.errors) {
      setUserState(state => ({ ...state, errors: response.errors }))
      return
    }

    navigate('/users')
  }

  const handleSaveError = error => {
    setUserState(state => ({
      ...state,
      errors: [error.message || 'An error occurred while saving the user'],
    }))
  }

  const createUserMutation = useCreateUser({ onError: handleSaveError, onSuccess: handleSaveSuccess })
  const updateUserMutation = useUpdateUser({ onError: handleSaveError, onSuccess: handleSaveSuccess })

  useEffect(() => {
    if (isConfigurationsError || isUserError) navigate('/users/sign_in')
  }, [isConfigurationsError, isUserError, navigate])

  useEffect(() => {
    if (isNewUser) {
      setUserState(initialUserState)
      setLoadedUserId(userId)
    } else if (user) {
      setUserState(state => ({ ...state, ...user }))
      setLoadedUserId(userId)
    }
  }, [isNewUser, user, userId])

  const setField = (field, value) => setUserState(state => ({ ...state, [field]: value }))

  const handleSaveUser = () => {
    setUserState(state => ({ ...state, errors: [] }))
    const payload = buildEditableUserPayload(userState)

    if (isNewUser) {
      createUserMutation.mutate(payload)
    } else {
      updateUserMutation.mutate({ endpointParams: { id: userId }, payload })
    }
  }

  const options = configurations.map(configuration => ({ label: configuration.name, value: configuration.id }))
  const selectedConfiguration = options.find(option => (
    option.value === userState.configurationId || option.value === userState.configuration?.id
  )) || null

  const isLoading = isConfigurationsLoading || (!isNewUser && (isUserLoading || loadedUserId !== userId))
  const isPending = createUserMutation.isPending || updateUserMutation.isPending

  return (
    <>
      <PageHeader
        subtitle={isNewUser ? 'Invite a team member and assign their initial access.' : 'Update this team member’s role, sign-in method, and configuration.'}
        title={isNewUser ? 'Add User' : 'Edit User'}
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : <Form className="tw:mx-auto tw:max-w-form" onSubmit={event => { event.preventDefault(); handleSaveUser() }}>
          <FormCard title="User Settings">
            {userState.errors.length > 0 && (
              <Alert color="danger" role="alert">
                <ul className="tw:grid tw:list-disc tw:gap-1 tw:pl-5">
                  {userState.errors.map((error, index) => <li key={index}>{error}</li>)}
                </ul>
              </Alert>
            )}
            <Field label="Email">
              <Input autoFocus name="email" onChange={event => setField('email', event.target.value)} type="email" value={userState.email} />
            </Field>
            <Field label="Configuration">
              <Select
                name="configuration"
                onChange={option => setField('configurationId', option?.value || 0)}
                options={options}
                placeholder="Select a configuration"
                value={selectedConfiguration}
              />
            </Field>
            <div className="tw:grid tw:grid-cols-2 tw:gap-x-3 tw:form:grid-cols-1">
              <Field label="Role">
                <Input name="role" onChange={event => setField('type', event.target.value)} type="select" value={userState.type}>
                  <option value="admin">Admin</option>
                  <option value="regular">Regular</option>
                </Input>
              </Field>
              <Field label="Authentication Method">
                <Input name="provider" onChange={event => setField('provider', event.target.value)} type="select" value={userState.provider}>
                  <option value="password">Password</option>
                  <option value="oauth">OAuth</option>
                </Input>
              </Field>
            </div>
            {userState.provider === 'password' && (
              <Field helper={isNewUser ? 'At least 6 characters.' : 'Leave blank to keep the current password.'} label="Password">
                <Input name="password" onChange={event => setField('password', event.target.value)} type="password" value={userState.password} />
              </Field>
            )}
          </FormCard>
          <FormActions className="tw:mt-5">
            {!isNewUser && (
              <DeleteUserConfirmModal id={userId} name={userState.email} onDeleteSuccess={() => navigate('/users')}>
                <Button className="tw:mr-auto" type="button" variant="danger">Delete User</Button>
              </DeleteUserConfirmModal>
            )}
            <Button onClick={() => navigate('/users')} type="button" variant="secondary">Cancel</Button>
            <Button disabled={isPending} type="submit">{isPending ? 'Saving…' : isNewUser ? 'Create User' : 'Save Changes'}</Button>
          </FormActions>
        </Form>}
      </PageContent>
    </>
  )
}
